import type { SnakeCasedPropertiesDeep } from 'type-fest';
import SqFetch from 'typescript/application/sq_fetch';
import { _handleResponse } from 'typescript/clients/utilities/handle_http_response';
import { CountryCode } from 'typescript/constants/localization';
import { getDomain } from 'typescript/utilities/environment';
import {
  GetAutocompleteCandidatesResponse,
  GetPlaceDetailsResponse,
} from 'rpc/model/squareup/address/service';
import {
  AddressValidationRequest,
  AddressValidationResponse,
} from 'typescript/types/api/address_validation';

export const SQUAREUP_API_BASE_URL = `https://${getDomain('api.')}`;
export const VALIDATE_ADDRESS_URL = '/shop/hardware/api/v1/addresses/validate';
export const GET_AUTOCOMPLETE_CANDIDATES_URL =
  '/1.0/address/get_autocomplete_candidates';
export const GET_PLACE_DETAILS_URL = '/1.0/address/get_place_details';
export const GET_JP_POSTAL_CODE_URL =
  '/shop/api/v1/checkout/postal_codes/show/jp';

/**
 * Validate address using Square's AddressService
 * @param address - address object
 * @returns an address validation result object with the following properties:
 * result, original_address, corrected_address, changed_fields, and address_service_statuses
 */
export const validateAddress = async ({
  address_validation_request: { address, save = false },
}: AddressValidationRequest): Promise<AddressValidationResponse> => {
  const response = await SqFetch.post(VALIDATE_ADDRESS_URL, {
    address_validation_request: {
      address,
      save,
    },
  });

  return _handleResponse(response);
};

/**
 * Get address autocomplete candidates
 * https://prototype.sqprod.co/#/docs/squareup.address.service.GetAutocompleteCandidatesRequest * @param partialAddress - partial address string
 * @param partialAddress - partial address string to query by
 * @param countryCode - country code
 * @param sessionToken - session token (getUuidByString using the partial address)
 * @returns Promise<any> - response from the google places api, returns a place_id that can be passed
 * to getPlaceDetails to get the full address
 */
export const getAutoCompleteCandidates = async ({
  partialAddress,
  countryCode,
  sessionToken,
}: {
  partialAddress: string;
  countryCode: CountryCode;
  sessionToken: string;
}): Promise<SnakeCasedPropertiesDeep<GetAutocompleteCandidatesResponse>> => {
  const response = await SqFetch.post(
    `${SQUAREUP_API_BASE_URL}${GET_AUTOCOMPLETE_CANDIDATES_URL}`,
    {
      partial_address: partialAddress,
      country_code: countryCode,
      session_token: sessionToken,
    },
  );

  return _handleResponse(response);
};

/**
 * Get place details from google places api using the place_id
 * https://prototype.sqprod.co/#/docs/squareup.address.service.GetPlaceDetailsRequest
 * @param placeId - place_id from the getAutoCompleteCandidates response
 * @param sessionToken - session token (getUuidByString using the partial address)
 * should be the same one as the one used in the getAutoCompleteCandidates request
 * @returns Promise<any> - response from the google places api, returns the full address
 */
export const getAutoCompletePlaceDetails = async ({
  placeId,
  sessionToken,
}: {
  placeId: string;
  sessionToken: string;
}): Promise<SnakeCasedPropertiesDeep<GetPlaceDetailsResponse>> => {
  const response = await SqFetch.post(
    `${SQUAREUP_API_BASE_URL}${GET_PLACE_DETAILS_URL}`,
    {
      place_id: placeId,
      session_token: sessionToken,
    },
  );

  return _handleResponse(response);
};

// TODO: figure out why this was implemented like this
// specfically for JP in the original client
// https://github.com/squareup/solidshop/pull/6302
export const getJpPostalCode = async ({
  postalCode,
}: {
  postalCode: string;
}): Promise<any> => {
  const response = await SqFetch.get(
    `${GET_JP_POSTAL_CODE_URL}/${postalCode.replaceAll(/\W/g, '')}`,
  );

  return _handleResponse(response);
};
